import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ClipboardText, ArrowClockwise, Warning, CheckCircle, Phone, XCircle, Clock, UserCircle, FilePdf, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

function ReportDownloader() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const download = (format) => {
    const API = window.location.origin;
    const a = document.createElement('a');
    a.href = `${API}/api/recovery/report?month=${month}&format=${format}`;
    a.target = '_blank';
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(`${format.toUpperCase()} downloading...`);
  };
  return (
    <div className="flex items-center gap-1 bg-gray-50 border rounded px-2 py-1">
      <input
        type="month"
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        className="text-xs h-6 bg-transparent focus:outline-none"
        data-testid="report-month"
      />
      <button onClick={() => download('pdf')} className="p-1 rounded hover:bg-red-50 text-red-600" title="Download PDF report" data-testid="report-pdf">
        <FilePdf size={14} weight="fill" />
      </button>
      <button onClick={() => download('xlsx')} className="p-1 rounded hover:bg-emerald-50 text-emerald-700" title="Download Excel report" data-testid="report-xlsx">
        <FileXls size={14} weight="fill" />
      </button>
    </div>
  );
}

export default function RecoveryQueuePage() {
  const { user } = useAuth();
  const location = useLocation();
  const isOwner = ['admin', 'owner'].includes(user?.role);
  const canReconcile = ['admin', 'owner', 'assistant'].includes(user?.role);

  const params = new URLSearchParams(location.search);
  const autoMode = params.get('auto'); // 'critical' → pre-filter + auto-select

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(autoMode === 'critical' ? 10 : 4);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ note: '', txn: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [autoApplied, setAutoApplied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get(`/recovery/queue?threshold_days=${threshold}`),
        api.get('/recovery/stats').catch(() => ({ data: null })),
      ]);
      setItems(q.data || []);
      setStats(s.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  // Auto-select all items when ?auto=critical landed (after first load)
  useEffect(() => {
    if (autoMode === 'critical' && isOwner && items.length > 0 && !autoApplied) {
      setSelected(new Set(items.map((b) => b.id)));
      setAutoApplied(true);
      toast.info(`${items.length} critical bookings selected — review and write off as needed`);
    }
  }, [autoMode, isOwner, items, autoApplied]);

  const openModal = (type, booking) => {
    setForm({ note: '', txn: '', reason: '' });
    setModal({ type, booking });
  };

  const submit = async () => {
    if (!modal) return;
    const { type, booking } = modal;
    setBusy(true);
    try {
      if (type === 'follow_up') {
        if (!form.note.trim()) { toast.error('Note required'); setBusy(false); return; }
        await api.post(`/recovery/${booking.id}/follow-up`, { note: form.note });
        toast.success('Follow-up noted');
      } else if (type === 'bulk_follow_up') {
        if (!form.note.trim()) { toast.error('Note required'); setBusy(false); return; }
        const ids = Array.from(selected);
        const r = await api.post('/recovery/bulk-follow-up', { booking_ids: ids, note: form.note });
        toast.success(`${r.data.updated}/${r.data.requested} bookings updated`);
        setSelected(new Set());
      } else if (type === 'bulk_write_off') {
        if (!form.reason.trim() || form.reason.trim().length < 5) {
          toast.error('Reason must be at least 5 characters');
          setBusy(false); return;
        }
        const ids = Array.from(selected);
        const r = await api.post('/recovery/bulk-write-off', { booking_ids: ids, reason: form.reason });
        toast.success(`${r.data.updated}/${r.data.requested} bookings written off`);
        setSelected(new Set());
      } else if (type === 'reconcile') {
        await api.post(`/recovery/${booking.id}/reconcile`, {
          note: form.note || null,
          confirm_transaction_id: form.txn || null,
        });
        toast.success('Reconciled ✓');
      } else if (type === 'write_off') {
        if (!form.reason.trim() || form.reason.trim().length < 5) {
          toast.error('Reason must be at least 5 characters');
          setBusy(false); return;
        }
        await api.post(`/recovery/${booking.id}/write-off`, { reason: form.reason });
        toast.success('Written off');
      }
      setModal(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((b) => b.id)));
  };

  const selectGroup = (ids) => {
    setSelected(new Set(ids));
  };

  const filtered = items.filter((b) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (b.staff_name || '').toLowerCase().includes(s) ||
      (b.agency_name || '').toLowerCase().includes(s) ||
      (b.transaction_id || '').toLowerCase().includes(s) ||
      (b.customer_name || '').toLowerCase().includes(s)
    );
  });

  // Smart Group-By: compute distinct groups with >1 members so chips are useful
  const groupBy = (key) => {
    const map = new Map();
    filtered.forEach((b) => {
      const v = (b[key] || '').trim();
      if (!v) return;
      if (!map.has(v)) map.set(v, []);
      map.get(v).push(b.id);
    });
    return Array.from(map.entries())
      .filter(([, ids]) => ids.length > 1)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 8);
  };
  const groupsByCustomer = groupBy('customer_name');
  const groupsByAgency = groupBy('agency_name');
  const groupsByStaff = groupBy('staff_name');

  // Age buckets
  const ageBuckets = [
    { key: 'warn', label: '4-6 days', className: 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-800', filter: (b) => b.days_pending >= 4 && b.days_pending <= 6 },
    { key: 'high', label: '7-9 days', className: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-800', filter: (b) => b.days_pending >= 7 && b.days_pending <= 9 },
    { key: 'critical', label: '≥10 days critical', className: 'bg-red-50 hover:bg-red-100 border-red-300 text-red-800', filter: (b) => b.days_pending >= 10 },
  ].map(bucket => ({ ...bucket, ids: filtered.filter(bucket.filter).map(b => b.id) }))
    .filter(bucket => bucket.ids.length > 0);

  return (
    <div className="space-y-5" data-testid="recovery-queue-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ClipboardText size={26} weight="duotone" className="text-amber-600" />
          Booking Recovery Queue
          <span className="text-sm font-normal text-gray-500 ml-1">({items.length})</span>
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap items-center">
          <select className="h-8 text-xs border rounded px-2" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} data-testid="threshold-select">
            <option value={0}>All pending</option>
            <option value={4}>Pending &ge; 4 days</option>
            <option value={7}>Pending &ge; 7 days</option>
            <option value={10}>Critical &ge; 10 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} className="gap-1"><ArrowClockwise size={14} /> Refresh</Button>
          <ReportDownloader />
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="stat-aged-4">
            <div className="text-[11px] uppercase text-amber-700 font-semibold">≥ 4 Days</div>
            <div className="text-2xl font-bold text-amber-900">{stats.aged_4_days}</div>
            <div className="text-[10px] text-amber-600">Needs attention</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="stat-aged-10">
            <div className="text-[11px] uppercase text-red-700 font-semibold flex items-center gap-1"><Warning size={11} /> ≥ 10 Days</div>
            <div className="text-2xl font-bold text-red-900">{stats.aged_10_days}</div>
            <div className="text-[10px] text-red-600">Critical — consider write-off</div>
          </div>
          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3">
            <div className="text-[11px] uppercase text-fuchsia-700 font-semibold">At Risk</div>
            <div className="text-xl font-bold text-fuchsia-900">{fmt(stats.at_risk_amount)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-[11px] uppercase text-emerald-700 font-semibold">Reconciled</div>
            <div className="text-2xl font-bold text-emerald-900">{stats.reconciled}</div>
          </div>
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
            <div className="text-[11px] uppercase text-gray-700 font-semibold">Written Off</div>
            <div className="text-2xl font-bold text-gray-900">{stats.written_off}</div>
          </div>
        </div>
      )}

      {/* Top staff panel */}
      {stats?.top_staff?.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="bg-amber-50 px-4 py-2 border-b text-sm font-semibold text-amber-900 flex items-center gap-2">
            <UserCircle size={16} /> Staff with most pending items
          </div>
          <div className="flex flex-wrap gap-2 p-3">
            {stats.top_staff.map((s) => (
              <div key={s.staff_id} className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-xs">
                <b className="text-amber-900">{s.staff_name}</b>
                <span className="text-amber-700 ml-2">{s.count} pending • {fmt(s.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Smart Group-By chips */}
      {(groupsByCustomer.length > 0 || groupsByAgency.length > 0 || groupsByStaff.length > 0 || ageBuckets.length > 0) && (
        <div className="bg-white border rounded-lg p-3 space-y-2" data-testid="smart-groups">
          <div className="text-[11px] font-semibold text-gray-500 uppercase">Smart Select — one click to select all matching</div>
          {ageBuckets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[11px] text-gray-500 w-20">By Age:</span>
              {ageBuckets.map((bucket) => (
                <button
                  key={bucket.key}
                  onClick={() => selectGroup(bucket.ids)}
                  className={`${bucket.className} border px-2 py-0.5 rounded-full text-[11px] transition`}
                  data-testid={`group-age-${bucket.key}`}
                >
                  {bucket.label} <b>({bucket.ids.length})</b>
                </button>
              ))}
            </div>
          )}
          {groupsByCustomer.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[11px] text-gray-500 w-20">Customer:</span>
              {groupsByCustomer.map(([name, ids]) => (
                <button key={`c-${name}`} onClick={() => selectGroup(ids)} className="bg-fuchsia-50 hover:bg-fuchsia-100 border border-fuchsia-200 text-fuchsia-800 px-2 py-0.5 rounded-full text-[11px] transition" data-testid={`group-customer-${name}`}>
                  {name} <b>({ids.length})</b>
                </button>
              ))}
            </div>
          )}
          {groupsByAgency.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[11px] text-gray-500 w-20">Agency:</span>
              {groupsByAgency.map(([name, ids]) => (
                <button key={`a-${name}`} onClick={() => selectGroup(ids)} className="bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-[11px] transition" data-testid={`group-agency-${name}`}>
                  {name} <b>({ids.length})</b>
                </button>
              ))}
            </div>
          )}
          {groupsByStaff.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[11px] text-gray-500 w-20">Staff:</span>
              {groupsByStaff.map(([name, ids]) => (
                <button key={`s-${name}`} onClick={() => selectGroup(ids)} className="bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full text-[11px] transition" data-testid={`group-staff-${name}`}>
                  {name} <b>({ids.length})</b>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search + bulk bar */}
      <div className="bg-white border rounded-lg p-2 flex items-center gap-2 flex-wrap">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by staff, agency, TXN, customer..."
          matchCount={filtered.length}
          totalCount={items.length}
          noun="bookings"
          testId="recovery-queue-search"
          className="flex-1 min-w-[260px]"
          inputClassName="pl-8 pr-7 h-8 text-xs"
        />
        {selected.size > 0 && (
          <>
            <div className="text-xs font-semibold text-amber-900 bg-amber-100 px-3 py-1.5 rounded" data-testid="bulk-selected-count">
              {selected.size} selected
            </div>
            <Button size="sm" onClick={() => { setForm({ note: '', txn: '', reason: '' }); setModal({ type: 'bulk_follow_up' }); }} className="bg-amber-600 hover:bg-amber-700 h-8 gap-1 text-xs" data-testid="bulk-followup-btn">
              <Phone size={12} /> Bulk Follow-up
            </Button>
            {isOwner && (
              <Button size="sm" onClick={() => { setForm({ note: '', txn: '', reason: '' }); setModal({ type: 'bulk_write_off' }); }} className="bg-red-600 hover:bg-red-700 h-8 gap-1 text-xs" data-testid="bulk-writeoff-btn">
                <XCircle size={12} /> Bulk Write-off
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="h-8 text-xs">Clear</Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600">
          Showing <b>{filtered.length}</b> bookings
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">🎉 No bookings in recovery queue</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-2 py-2 w-8 text-center">
                    <input type="checkbox"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 cursor-pointer accent-amber-600"
                      data-testid="select-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Staff</th>
                  <th className="px-3 py-2 text-left">Agency</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">TXN</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-center">Days</th>
                  <th className="px-3 py-2 text-left">Last Update</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const critical = b.days_pending >= 10;
                  const attemptsCount = (b.recovery_attempts || []).length;
                  const lastAttempt = attemptsCount ? b.recovery_attempts[attemptsCount - 1] : null;
                  const isSel = selected.has(b.id);
                  return (
                    <tr key={b.id} className={`border-t hover:bg-amber-50/30 ${isSel ? 'bg-amber-50/60' : ''}`} data-testid={`recovery-row-${b.id}`}>
                      <td className="px-2 py-1.5 text-center">
                        <input type="checkbox" checked={isSel} onChange={() => toggleSelect(b.id)} className="w-3.5 h-3.5 cursor-pointer accent-amber-600" data-testid={`select-${b.id}`} />
                      </td>
                      <td className="px-3 py-1.5 text-xs">{b.booking_date || '—'}</td>
                      <td className="px-3 py-1.5 text-xs font-medium">{b.staff_name}</td>
                      <td className="px-3 py-1.5 text-xs">{b.agency_name}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-600">{b.customer_name || '—'}</td>
                      <td className="px-3 py-1.5 text-xs font-mono text-gray-600">{b.transaction_id}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{fmt(b.total_amount)}</td>
                      <td className={`px-3 py-1.5 text-center font-bold ${critical ? 'text-red-600' : 'text-amber-600'}`}>
                        {b.days_pending}d{critical && <Warning size={12} weight="fill" className="inline ml-0.5" />}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] text-gray-500">
                        {lastAttempt ? (
                          <>
                            <div><b>{lastAttempt.action}</b> by {lastAttempt.by_name}</div>
                            <div className="text-gray-400">{fmtTime(lastAttempt.at)}</div>
                          </>
                        ) : <span className="text-gray-400">No follow-up yet</span>}
                        {attemptsCount > 0 && <div className="text-[9px] text-gray-400">{attemptsCount} attempt{attemptsCount > 1 ? 's' : ''}</div>}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => openModal('follow_up', b)} className="h-7 text-[10px] gap-1 px-2" data-testid={`followup-${b.id}`}>
                            <Phone size={11} /> Follow-up
                          </Button>
                          {canReconcile && (
                            <Button size="sm" onClick={() => openModal('reconcile', b)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[10px] gap-1 px-2" data-testid={`reconcile-${b.id}`}>
                              <CheckCircle size={11} /> Reconcile
                            </Button>
                          )}
                          {isOwner && (
                            <Button size="sm" variant="outline" onClick={() => openModal('write_off', b)} className="border-red-300 text-red-700 hover:bg-red-50 h-7 text-[10px] gap-1 px-2" data-testid={`writeoff-${b.id}`}>
                              <XCircle size={11} /> Write-off
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent data-testid="recovery-modal">
          {modal && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {modal.type === 'follow_up' && <><Phone size={16} className="inline mr-2" />Add Follow-up</>}
                  {modal.type === 'bulk_follow_up' && <><Phone size={16} className="inline mr-2 text-amber-600" />Bulk Follow-up ({selected.size} bookings)</>}
                  {modal.type === 'reconcile' && <><CheckCircle size={16} className="inline mr-2 text-emerald-600" />Reconcile Booking</>}
                  {modal.type === 'write_off' && <><XCircle size={16} className="inline mr-2 text-red-600" />Write Off Booking (Owner only)</>}
                  {modal.type === 'bulk_write_off' && <><XCircle size={16} className="inline mr-2 text-red-600" />Bulk Write Off ({selected.size} bookings)</>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {modal.booking && (
                  <div className="bg-gray-50 p-2 rounded text-xs space-y-0.5">
                    <div><b>{modal.booking.staff_name}</b> → {modal.booking.agency_name}</div>
                    <div>TXN <span className="font-mono">{modal.booking.transaction_id}</span> • {fmt(modal.booking.total_amount)}</div>
                    <div className="text-amber-600"><Clock size={10} className="inline mr-1" />{modal.booking.days_pending} days pending</div>
                  </div>
                )}
                {modal.type === 'bulk_follow_up' && (
                  <div className="bg-amber-50 p-2 rounded text-xs border border-amber-200">
                    Same note will be added to <b>{selected.size} bookings</b>. Each gets its own audit entry.
                  </div>
                )}
                {modal.type === 'bulk_write_off' && (
                  <div className="bg-red-50 p-2 rounded text-xs border border-red-300">
                    ⚠️ <b>{selected.size} bookings</b> will be written off as non-recoverable loss. This cannot be undone.
                  </div>
                )}
                {(modal.type === 'follow_up' || modal.type === 'bulk_follow_up') && (
                  <div>
                    <label className="text-xs font-medium">Follow-up note</label>
                    <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g., Called customer, will pay by tomorrow" data-testid="modal-note" />
                  </div>
                )}
                {modal.type === 'reconcile' && (
                  <>
                    <div>
                      <label className="text-xs font-medium">Confirm Transaction ID (optional)</label>
                      <Input value={form.txn} onChange={(e) => setForm({ ...form, txn: e.target.value.toUpperCase() })} placeholder="TXN..." data-testid="modal-txn" />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Note (optional)</label>
                      <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="modal-note" />
                    </div>
                  </>
                )}
                {(modal.type === 'write_off' || modal.type === 'bulk_write_off') && (
                  <div>
                    <label className="text-xs font-medium">Write-off reason <span className="text-red-500">*</span></label>
                    <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g., Customer unreachable for 15 days, amount is non-recoverable" data-testid="modal-reason" />
                    <p className="text-[10px] text-red-600 mt-1">⚠️ This action cannot be undone and will be audit-logged.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} className={
                  modal.type === 'reconcile' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  (modal.type === 'write_off' || modal.type === 'bulk_write_off') ? 'bg-red-600 hover:bg-red-700' : ''
                } data-testid="modal-submit">
                  {busy ? 'Saving...' : 'Confirm'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
